import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { BRAND } from "@/lib/brand";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) {
    const next = location.state?.from?.pathname || "/dashboard";
    return <Navigate to={next} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen grid lg:grid-cols-2 bg-[#F8FAFC]">
      {/* Left form */}
      <div className="flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-10" data-testid="login-brand-block">
            <img
              src={BRAND.logoUrl}
              alt="Solemnity"
              className="w-56 sm:w-64 md:w-72 lg:w-80 h-auto object-contain drop-shadow-sm"
            />
            <div className="text-[10px] uppercase tracking-wider text-gray-400 mt-3">
              HRMS · {BRAND.tagline}
            </div>
          </div>

          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#0F172A]">
            Welcome back.
          </h1>
          <p className="text-sm text-gray-500 mt-1 mb-8">
            Sign in to manage your team, payroll, and people operations.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-gray-500">
                Email or Employee ID
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="text"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11 mt-1.5"
                placeholder="you@company.com  ·  or  ·  EMP-0005"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-gray-500">
                Password
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11 mt-1.5"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#F26B3A] hover:bg-[#d85d2f] text-white font-medium"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <span className="flex items-center gap-2">Sign in <ArrowRight className="h-4 w-4" /></span>
              )}
            </Button>
          </form>

        </div>
      </div>

      {/* Right hero */}
      <div
        className="hidden lg:block relative login-hero"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(15,23,42,0.92), rgba(30,136,229,0.55)), url('https://images.unsplash.com/photo-1531972111231-7482a960e109?crop=entropy&cs=srgb&fm=jpg&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <blockquote className="font-display text-3xl leading-snug max-w-md">
            &ldquo;Solemnity unified hiring, onboarding, payroll, and compliance into one calm, beautiful workspace.&rdquo;
          </blockquote>
          <div className="mt-4 text-sm opacity-80">— Internal review, Q1 2026</div>
        </div>
      </div>
    </div>
  );
}
